import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Controller, useFieldArray, useWatch } from 'react-hook-form'
import { Avatar, Card, CardActionArea, CardHeader } from '@mui/material'

import {
  Button,
  Checkbox,
  Fields,
  FormControl,
  FormControlLabel,
  IconButton,
  Icons,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Row,
  Select,
  Stack,
  Text,
  Tooltip,
  useAlert,
  validations,
} from '..'
import { useBrandingContext } from '../../context'
import { PROVINCES_LIST } from '../../constants'
import { fromAmountField } from '../../utils'
import { openApiFileWithData } from '../../utils/open-api-file'

/**
 * Securities path types - determines which flow the donor follows
 */
export const SECURITIES_PATH = {
  ADVISOR: 'advisor',
  SELF_DIRECTED: 'self_directed',
}

/**
 * Path selection screen shown before securities form fields
 */
export function SecuritiesPathSelection({ onSelect, selectedPath }) {
  const branding = useBrandingContext() || {}
  const primaryColor = branding.primaryColor || '#61bee8'
  const paths = [
    {
      value: SECURITIES_PATH.ADVISOR,
      title: 'I work with a financial advisor',
      tooltip: 'My advisor will handle the securities transfer.',
      Icon: Icons.CircleUser,
    },
    {
      value: SECURITIES_PATH.SELF_DIRECTED,
      title: 'I use a self-directed investment platform',
      tooltip: 'I will submit the transfer myself.',
      Icon: Icons.Investments,
    },
  ]

  return (
    <Stack spacing={2}>
      <Text.Body>Choose the option that best describes your situation</Text.Body>
      {paths.map((path) => {
        const isSelected = selectedPath === path.value
        return (
          <Tooltip key={path.value} title={path.tooltip} placement="top" arrow>
            <Card
              sx={{
                width: '100%',
                border: isSelected ? `2px solid ${primaryColor}` : '1px solid #e0e0e0',
                borderLeft: isSelected ? `6px solid ${primaryColor}` : `4px solid ${primaryColor}`,
                cursor: 'pointer',
                backgroundColor: isSelected ? `${primaryColor}14` : 'inherit',
                boxShadow: isSelected ? `0 2px 8px ${primaryColor}40` : 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: isSelected ? `${primaryColor}1F` : 'action.hover',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                },
              }}
              onClick={() => onSelect(path.value)}
            >
              <CardActionArea>
                <CardHeader
                  avatar={
                    <Avatar
                      sx={{
                        backgroundColor: isSelected ? primaryColor : 'white',
                        padding: '8px',
                        width: '52px',
                        height: '52px',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <path.Icon
                        style={{
                          fontSize: '48px',
                          color: isSelected ? 'white' : primaryColor,
                          backgroundColor: 'transparent',
                        }}
                      />
                    </Avatar>
                  }
                  action={
                    isSelected && (
                      <Icons.Success
                        sx={{
                          color: primaryColor,
                          fontSize: '28px',
                          mr: 1,
                          mt: 1.5,
                        }}
                      />
                    )
                  }
                  title={path.title}
                  titleTypographyProps={{
                    variant: 'subtitle1',
                    color: isSelected ? 'primary' : 'text.primary',
                    fontWeight: 600,
                  }}
                />
              </CardActionArea>
            </Card>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

/**
 * Advisor Details Form - Step 2A for donors who work with a financial advisor
 * Shows a simplified form with just the essential advisor information
 */
export function AdvisorDetailsForm({ register, control, formState, hideIntendedUse = false }) {
  const { errors = {} } = formState

  return (
    <Stack spacing={2}>
      <Text.H6>Advisor Details</Text.H6>
      <Text.Body>We&apos;ll use this information to contact your advisor and begin the securities transfer.</Text.Body>
      <Fields.Text
        inputProps={{ 'data-testid': 'advisorName' }}
        type="text"
        label="Advisor Name *"
        error={!!errors?.advisorName}
        helperText={errors?.advisorName?.message}
        {...register('advisorName', { ...validations.required })}
      />
      <Fields.Text
        inputProps={{ 'data-testid': 'advisorEmail' }}
        type="text"
        label="Advisor Email *"
        error={!!errors?.advisorEmail}
        helperText={errors?.advisorEmail?.message}
        {...register('advisorEmail', { ...validations.required, ...validations.email })}
      />
      <Fields.Text
        inputProps={{ 'data-testid': 'custodianName' }}
        type="text"
        label="Advisor Firm or Brokerage *"
        error={!!errors?.custodianName}
        helperText={errors?.custodianName?.message}
        {...register('custodianName', {
          required: 'This field is required',
          validate: {
            noSpecialCharacters: (value) => /^[a-zA-Z0-9\s]*$/.test(value) || 'No special characters allowed',
          },
        })}
      />
      <Controller
        control={control}
        name="intendedAmount"
        defaultValue=""
        rules={{
          required: 'This field is required',
          validate: {
            positive: (value) => parseFloat(value) > 0 || 'Must be a positive number',
          },
        }}
        render={({ field: { ref, ...field } }) => (
          <Fields.Text
            {...field}
            inputRef={ref}
            label="Intended Approximate Contribution Amount (estimate is sufficient) *"
            error={!!errors?.intendedAmount}
            helperText={errors?.intendedAmount?.message}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputComponent: Fields.Amount,
            }}
          />
        )}
      />

      {!hideIntendedUse && (
        <FormControl error={!!errors?.intendedUse} fullWidth>
          <InputLabel id="intendedUse-label">Intended Use of Contribution *</InputLabel>
          <Controller
            control={control}
            name="intendedUse"
            defaultValue=""
            rules={{ required: 'This field is required' }}
            render={({ field }) => (
              <Select
                {...field}
                labelId="intendedUse-label"
                label="Intended Use of Contribution *"
                data-testid="intendedUse"
              >
                <MenuItem value="liquidate">Liquidate for immediate giving (Giving Wallet)</MenuItem>
                <MenuItem value="invest">Invest with my advisor for growth (Investment Account)</MenuItem>
                <MenuItem value="discuss">I will discuss with my advisor</MenuItem>
              </Select>
            )}
          />
          {errors?.intendedUse && (
            <Text.Body sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
              {errors.intendedUse.message}
            </Text.Body>
          )}
        </FormControl>
      )}

      <Stack spacing={1} sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Text.H6>What Happens Next</Text.H6>
        <Text.Body>
          Once you submit this form, we&apos;ll email your advisor with instructions to initiate the securities transfer
          to GiveWise.
        </Text.Body>
        <Text.Body>If your advisor already works with GiveWise, they&apos;ll be notified of your request.</Text.Body>
        <Text.Body>If they&apos;re new to GiveWise, they&apos;ll receive an invitation to get set up.</Text.Body>
        <Text.Body sx={{ fontStyle: 'italic', mt: 1 }}>
          Please be aware that by completing this form, you are authorizing the above noted advisor to complete security
          contributions on your behalf.
        </Text.Body>
      </Stack>
    </Stack>
  )
}

/**
 * @typedef SecurityValues
 * @property {string} securitiesDescription - Securities Description
 * @property {string} fundSymbolCUSIP - Fund Symbol/CUSIP
 * @property {string|number} quantity - Quantity
 * @property {string|number} amount - Approx Value
 */

/** @type {SecurityValues[]} */
export const defaultSecurities = [
  {
    securitiesDescription: '',
    fundSymbolCUSIP: '',
    quantity: 0,
    amount: 0,
  },
]

/**
 * Utility for calculating the total approx value of a list of securities
 *
 * @param {SecurityValues[]} securities - The list of securities
 * @returns {number} The total approx value
 */
export function calculateSumForSecurities(securities) {
  return securities.reduce((total, sec) => {
    const amount = fromAmountField(sec.amount || '0')

    return total + amount
  }, 0)
}

/**
 * Intended Use selector for advisor-led contributions (advisor impersonating client)
 * Only shows Liquidate and Invest options (no "discuss" since the advisor is making the choice)
 */
export function AdvisorIntendedUseSelect({ control, formState }) {
  const { errors = {} } = formState

  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Text.H6>Intended Use of Contribution</Text.H6>
      <FormControl error={!!errors?.intendedUse} fullWidth>
        <InputLabel id="intendedUse-label">Intended Use of Contribution *</InputLabel>
        <Controller
          control={control}
          name="intendedUse"
          defaultValue=""
          rules={{ required: 'This field is required' }}
          render={({ field }) => (
            <Select
              {...field}
              labelId="intendedUse-label"
              label="Intended Use of Contribution *"
              data-testid="intendedUse"
            >
              <MenuItem value="liquidate">Liquidate for immediate giving (Giving Wallet)</MenuItem>
              <MenuItem value="invest">Invest with my advisor for growth (Investment Account)</MenuItem>
            </Select>
          )}
        />
        {errors?.intendedUse && (
          <Text.Body sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>{errors.intendedUse.message}</Text.Body>
        )}
      </FormControl>
    </Stack>
  )
}

// FLIGHT-546: pattern that suggests a donor has typed rep-code-like content
// into the Advisor Name field instead of using the dedicated Dealer Rep Code
// input. Matches the literal phrase "rep code" (any spacing/case) or a
// digit run followed by a slash/dash and another token (e.g. "9184/BC901").
const REP_CODE_HINT_PATTERN = /(rep\s*code)|(\b\d{3,}\s*[/-]\s*\w+\b)/i

export function DeliveringCustodianInformation({ register, control, formState, shrinkLabels = false, intendedUse }) {
  const { errors = {} } = formState
  const labelProps = shrinkLabels ? { shrink: true } : {}
  const isInvest = intendedUse === 'invest'

  // FLIGHT-546 nudge: watch the Advisor Name input and surface a soft hint
  // (in the helperText slot) if the donor types something that looks like a
  // rep code. Non-blocking - the form still submits.
  const advisorNameValue = useWatch({ control, name: 'advisorName', defaultValue: '' })
  const advisorNameLooksLikeRepCode =
    typeof advisorNameValue === 'string' && REP_CODE_HINT_PATTERN.test(advisorNameValue)

  return (
    <Stack sx={{ mb: 2 }}>
      <Stack sx={{ display: 'flex', mb: 2 }}>
        <Text.H6 sx={{ mb: -2 }}>{isInvest ? 'Custodian Information' : 'Delivering Custodian Information'}</Text.H6>
      </Stack>

      <Fields.Text
        inputProps={{ 'data-testid': 'custodianName' }}
        InputLabelProps={labelProps}
        type="text"
        label="Name of Securities Firm *"
        error={!!errors?.custodianName}
        helperText={errors?.custodianName?.message}
        {...register('custodianName', {
          required: 'This field is required',
          validate: {
            noSpepcialCharacters: (value) => /^[a-zA-Z0-9\s]*$/.test(value) || 'No special characters allowed',
          },
        })}
      />
      <Row>
        <Fields.Text
          inputProps={{ 'data-testid': 'advisorEmail' }}
          InputLabelProps={labelProps}
          type="text"
          label="Advisor's Email *"
          error={!!errors?.advisorEmail}
          helperText={errors?.advisorEmail?.message}
          {...register('advisorEmail', { ...validations.required, ...validations.email })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'advisorPhone' }}
          InputLabelProps={labelProps}
          type="text"
          label="Advisor's Phone Number *"
          error={!!errors.advisorPhone}
          helperText={errors?.advisorPhone?.message}
          {...register('advisorPhone', { ...validations.required, ...validations.phoneNumber })}
        />
      </Row>
      <Row>
        <Fields.Text
          inputProps={{ 'data-testid': 'advisorName' }}
          InputLabelProps={labelProps}
          type="text"
          label="Contact Name (Advisor) *"
          error={!!errors.advisorName}
          helperText={
            errors?.advisorName?.message ||
            (advisorNameLooksLikeRepCode
              ? "Looks like a rep code. Please use the 'Advisor Dealer Rep Code' field for those details."
              : '')
          }
          {...register('advisorName', { ...validations.required })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'advisorDealerRepCode' }}
          InputLabelProps={labelProps}
          type="text"
          label="Advisor Dealer Rep Code"
          error={!!errors.advisorDealerRepCode}
          helperText={errors?.advisorDealerRepCode?.message}
          {...register('advisorDealerRepCode')}
        />
      </Row>

      <Fields.Text
        inputProps={{ 'data-testid': 'custodianAddress' }}
        InputLabelProps={labelProps}
        type="text"
        label="Securities Dealer/ Custodian Address *"
        error={!!errors.address?.lineOne}
        helperText={errors?.address?.lineOne?.message}
        {...register('address.lineOne', { ...validations.required })}
      />
      <Row>
        <Fields.Text
          inputProps={{ 'data-testid': 'city' }}
          InputLabelProps={labelProps}
          type="text"
          label="City *"
          error={!!errors.address?.city}
          helperText={errors?.address?.city?.message}
          {...register('address.city', { ...validations.required })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'province' }}
          InputLabelProps={labelProps}
          type="text"
          label="Province/State *"
          error={!!errors.address?.province}
          helperText={errors?.address?.province?.message}
          {...register('address.province', { ...validations.required })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'postalCode' }}
          InputLabelProps={labelProps}
          type="text"
          label="Postal Code/ZIP *"
          error={!!errors.address?.postalCode}
          helperText={errors?.address?.postalCode?.message}
          {...register('address.postalCode', { ...validations.required })}
        />
      </Row>
      <Fields.Text
        inputProps={{ 'data-testid': 'accountNumber' }}
        InputLabelProps={labelProps}
        type="text"
        label={
          isInvest ? 'Surrendering Investment Account Number *' : "Donor's Dealer/Custodial Investment Account Number *"
        }
        error={!!errors.accountNumber}
        helperText={errors?.accountNumber?.message}
        {...register('accountNumber', { ...validations.required })}
      />

      {isInvest && (
        <>
          <Stack sx={{ display: 'flex', mb: 2, mt: 2 }}>
            <Text.H6>Recipient: GiveWise Investment Account Information</Text.H6>
          </Stack>
          <Fields.Text
            inputProps={{ 'data-testid': 'investmentAccountNumber' }}
            InputLabelProps={labelProps}
            type="text"
            label="Investment Account Number Associated With This Client *"
            error={!!errors.investmentAccountNumber}
            helperText={errors?.investmentAccountNumber?.message}
            {...register('investmentAccountNumber', { ...validations.required })}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'investmentAccountName' }}
            InputLabelProps={labelProps}
            type="text"
            label="Investment Account Name *"
            error={!!errors.investmentAccountName}
            helperText={errors?.investmentAccountName?.message}
            {...register('investmentAccountName', { ...validations.required })}
          />
        </>
      )}
    </Stack>
  )
}

/**
 * Custodian info for self-directed path
 */
export function SelfDirectedCustodianInfo({ register, formState }) {
  const { errors = {} } = formState

  return (
    <Stack sx={{ mb: 2 }}>
      <Text.Body sx={{ mb: 2 }}>Provide the details below so we can receive your investment transfer.</Text.Body>
      <Text.H6>Delivering Custodian Information</Text.H6>

      <Fields.Text
        inputProps={{ 'data-testid': 'custodianAddress' }}
        type="text"
        label="Securities Dealer/ Custodian Address *"
        error={!!errors.address?.lineOne}
        helperText={errors?.address?.lineOne?.message}
        {...register('address.lineOne', { ...validations.required })}
      />
      <Row>
        <Fields.Text
          inputProps={{ 'data-testid': 'city' }}
          type="text"
          label="City *"
          error={!!errors.address?.city}
          helperText={errors?.address?.city?.message}
          {...register('address.city', { ...validations.required })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'province' }}
          type="text"
          label="Province/State *"
          error={!!errors.address?.province}
          helperText={errors?.address?.province?.message}
          {...register('address.province', { ...validations.required })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'postalCode' }}
          type="text"
          label="Postal Code/ZIP *"
          error={!!errors.address?.postalCode}
          helperText={errors?.address?.postalCode?.message}
          {...register('address.postalCode', { ...validations.required })}
        />
      </Row>
      <Fields.Text
        inputProps={{ 'data-testid': 'accountNumber' }}
        type="text"
        label="Account Number *"
        error={!!errors.accountNumber}
        helperText={errors?.accountNumber?.message}
        {...register('accountNumber', { ...validations.required })}
      />
    </Stack>
  )
}

/**
 * Advisor dealer rep code field only
 */
export function AdvisorDealerRepCode({ register, formState }) {
  const { errors = {} } = formState

  return (
    <Fields.Text
      inputProps={{ 'data-testid': 'advisorDealerRepCode' }}
      type="text"
      label="Advisor Dealer Rep Code"
      error={!!errors.advisorDealerRepCode}
      helperText={errors?.advisorDealerRepCode?.message}
      {...register('advisorDealerRepCode')}
      sx={{ mb: 2 }}
    />
  )
}

export function SecuritiesToTransferInKind({ required = false, register, control, formState }) {
  const { isSubmitting, errors = {} } = formState
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'securities',
  })

  return (
    <Stack sx={{ mb: 2 }}>
      <Text.H6 sx={{ display: 'flex' }}>Securities To Transfer In Kind{required && ' *'}</Text.H6>

      {fields.map((field, index) => (
        <Row key={field.id}>
          <Fields.Text
            inputProps={{ 'data-testid': `securities[${index}].securitiesDescription` }}
            type="text"
            multiline
            sx={{ minWidth: '300px' }}
            label="Securities Description *"
            error={!!errors?.securities?.[index]?.securitiesDescription}
            helperText={errors?.securities?.[index]?.securitiesDescription?.message}
            {...register(`securities[${index}].securitiesDescription`, { ...validations.required })}
          />
          <Fields.Text
            inputProps={{ 'data-testid': `securities[${index}].fundSymbolCUSIP` }}
            type="text"
            label="Fund Symbol/CUSIP *"
            error={!!errors?.securities?.[index]?.fundSymbolCUSIP}
            helperText={errors?.securities?.[index]?.fundSymbolCUSIP?.message}
            {...register(`securities[${index}].fundSymbolCUSIP`, { ...validations.required })}
          />
          <Controller
            control={control}
            name={`securities[${index}].quantity`}
            defaultValue={0}
            rules={{
              required: 'This field is required',
              validate: {
                positive: (value) => parseFloat(value) > 0 || 'Must be a positive number',
              },
            }}
            // eslint-disable-next-line no-shadow
            render={({ field: { ref, ...field } }) => (
              <Fields.Text
                {...field}
                inputRef={ref}
                label="Quantity *"
                error={!!errors?.securities?.[index]?.quantity}
                helperText={errors?.securities?.[index]?.quantity?.message}
                InputProps={{
                  inputComponent: Fields.Amount,
                  inputProps: { decimalScale: 4 },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name={`securities[${index}].amount`}
            defaultValue={0}
            rules={{
              required: 'This field is required',
              validate: {
                positive: (value) => parseFloat(value) > 0 || 'Must be a positive number',
                min: (value) => parseFloat(value) >= 1 || 'Must be at least 1',
              },
            }}
            // eslint-disable-next-line no-shadow
            render={({ field: { ref, ...field } }) => (
              <Fields.Text
                {...field}
                inputRef={ref}
                label="Approx Total Value *"
                name={`securities[${index}].amount`}
                error={!!errors?.securities?.[index]?.amount}
                helperText={errors?.securities?.[index]?.amount?.message}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputComponent: Fields.Amount,
                }}
              />
            )}
          />
          {fields.length > 1 && (
            <IconButton onClick={() => remove(index)} size="small">
              <Icons.Trash fontSize="small" />
            </IconButton>
          )}
        </Row>
      ))}

      <Button
        type="button"
        disabled={isSubmitting}
        onClick={() => append({ securitiesDescription: '', fundSymbolCUSIP: '', quantity: 0, amount: 0 })}
      >
        <Icons.Plus style={{ marginRight: '10px' }} />
        Add Additional Security
      </Button>
    </Stack>
  )
}

export function Acknowledgement({ control, formState }) {
  const { errors = {} } = formState
  return (
    <>
      <Controller
        control={control}
        rules={{ required: 'You must acknowledge before proceeding.' }}
        name="pageLanguageConfirmed"
        render={({ field: { value, onChange, ...field } }) => (
          <Row justifyContent="space-between" spacing={4} sx={{ marginTop: '2rem!important' }}>
            <Checkbox
              {...field}
              sx={{ marginTop: -1, marginRight: -1 }}
              checked={value}
              onChange={(e) => {
                onChange(e.target.checked)
              }}
            />
            <Text.Body>
              I acknowledge that GiveWise Foundation Canada (GWFC) cannot control when a security is received. To ensure
              receipting within a specific calendar year, please allow sufficient time, as GWFC is not responsible for
              delays. I understand that a cost recovery fee according to the fee schedule will be deducted from the
              donation proceeds.
            </Text.Body>
          </Row>
        )}
      />
      {errors?.pageLanguageConfirmed && (
        <Text.Body sx={{ color: 'red', marginTop: '0.5rem' }}>{errors.pageLanguageConfirmed.message}</Text.Body>
      )}
    </>
  )
}

/**
 * Intended Use Acknowledgement - shown on the advisor completion page
 * Renders conditional UI based on the donor's intendedUse selection
 */
export function IntendedUseAcknowledgement({ intendedUse, control, formState }) {
  const { errors = {} } = formState

  if (intendedUse === 'liquidate') {
    return (
      <>
        <Controller
          control={control}
          rules={{ required: 'You must acknowledge before proceeding.' }}
          name="intendedUseAcknowledged"
          render={({ field: { value, onChange, ...field } }) => (
            <Row alignItems="flex-start" spacing={1}>
              <Checkbox
                {...field}
                sx={{ marginTop: -1, marginRight: -1 }}
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
              />
              <Text.Body>
                I acknowledge that the donor requests this transfer be sent to GiveWise&apos;s BMO Investorline
                Brokerage
              </Text.Body>
            </Row>
          )}
        />
        {errors?.intendedUseAcknowledged && (
          <Text.Body sx={{ color: 'red', marginTop: '0.5rem' }}>{errors.intendedUseAcknowledged.message}</Text.Body>
        )}
      </>
    )
  }

  if (intendedUse === 'invest') {
    return (
      <>
        <Controller
          control={control}
          rules={{ required: 'You must acknowledge before proceeding.' }}
          name="intendedUseAcknowledged"
          render={({ field: { value, onChange, ...field } }) => (
            <Row alignItems="flex-start" spacing={1}>
              <Checkbox
                {...field}
                sx={{ marginTop: -1, marginRight: -1 }}
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
              />
              <Text.Body>
                I acknowledge that the donor requests this security be transferred in-kind, internally into the existing
                GiveWise investment account associated with this donor
              </Text.Body>
            </Row>
          )}
        />
        {errors?.intendedUseAcknowledged && (
          <Text.Body sx={{ color: 'red', marginTop: '0.5rem' }}>{errors.intendedUseAcknowledged.message}</Text.Body>
        )}
      </>
    )
  }

  if (intendedUse === 'discuss') {
    return (
      <Stack spacing={2}>
        <Text.Body>
          The donor has requested that the intended use of this transfer be discussed. Please contact your client and
          choose one of the options below.
        </Text.Body>
        <Controller
          control={control}
          rules={{ required: 'You must select an option before proceeding.' }}
          name="advisorIntendedUseChoice"
          render={({ field }) => (
            <FormControl error={!!errors?.advisorIntendedUseChoice}>
              <RadioGroup {...field}>
                <FormControlLabel
                  value="liquidate"
                  control={<Radio />}
                  label="Liquidate for immediate giving (Giving Wallet)"
                />
                <FormControlLabel
                  value="invest"
                  control={<Radio />}
                  label="Invest with my advisor for growth (Investment Account)"
                />
              </RadioGroup>
              {errors?.advisorIntendedUseChoice && (
                <Text.Body sx={{ color: 'red', marginTop: '0.5rem' }}>
                  {errors.advisorIntendedUseChoice.message}
                </Text.Body>
              )}
            </FormControl>
          )}
        />
      </Stack>
    )
  }

  return null
}

/**
 * Fetch Letter of Direction PDF contents
 */
export function fetchGeneratedLetterOfDirectionPdf({
  me,
  user,
  fundId,
  securities,
  deliveringCustodianInfo,
  contributionIds,
}) {
  const { profile } = me ?? {}
  const fundProfile = fundId
    ? me?.funds?.find((f) => f.id === Number(fundId))?.fundProfile
    : me?.funds?.find((f) => !f.name?.startsWith('charity-') && !f.closed)?.fundProfile

  return openApiFileWithData(`/pdf/generate/letter-of-direction/${contributionIds?.[0]}`, {
    data: {
      donorProfile: {
        firstName: profile?.firstName ?? user?.firstName,
        lastName: profile?.lastName ?? user?.lastName,
        middleInitials: profile?.middleInitials ?? user?.middleInitials,
        phone: profile?.phone ?? user?.phone,
        email: me?.email ?? user?.email,
        userType: fundProfile?.identityKind === 'business' ? 'corporation' : 'individual',
        portalName: fundProfile?.portalName ?? user?.portalName,
        isOrganization: me?.isOrganization ?? user?.isOrganization,
        corporateName: me?.corporateName ?? user?.corporateName,
        address: {
          lineOne: fundProfile?.address?.lineOne ?? user?.address?.lineOne,
          lineTwo: fundProfile?.address?.lineTwo ?? user?.address?.lineTwo,
          city: fundProfile?.address?.city ?? user?.address?.city,
          province: fundProfile?.address?.province ?? user?.address?.province,
          postalCode: fundProfile?.address?.postalCode ?? user?.address?.postalCode,
          country: fundProfile?.address?.country ?? user?.address?.country,
        },
      },
      accountNumber: deliveringCustodianInfo?.accountNumber,
      investmentAccountNumber: deliveringCustodianInfo?.investmentAccountNumber,
      investmentAccountName: deliveringCustodianInfo?.investmentAccountName,
      address: deliveringCustodianInfo?.address,
      advisorEmail: deliveringCustodianInfo?.advisorEmail,
      advisorName: deliveringCustodianInfo?.advisorName,
      advisorDealerRepCode: deliveringCustodianInfo?.advisorDealerRepCode,
      advisorPhone: deliveringCustodianInfo?.advisorPhone,
      custodianName: deliveringCustodianInfo?.custodianName,
      securities,
    },
  })
}

export function GeneratedLetterOfDirection({ letterOfDirectionPdf }) {
  const [fetchedPdf, setFetchedPdf] = useState(null)
  const [isFetching, setIsFetching] = useState(false)

  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const setAlertRef = useRef()
  useLayoutEffect(() => {
    setAlertRef.current = setAlert
  })
  useEffect(() => {
    // eslint-disable-next-line no-shadow -- useAlert does not implement proper memoization
    const setAlert = setAlertRef.current
    if (!letterOfDirectionPdf) return undefined

    const abortController = new AbortController()

    setFetchedPdf(null)
    setIsFetching(true)
    letterOfDirectionPdf
      .then((pdf) => {
        if (!abortController.signal.aborted) {
          setFetchedPdf(pdf)
        }
      })
      .catch((e) => {
        if (!abortController.signal.aborted) {
          setAlert({
            message: `Failed to create the Letter of Direction PDF: ${e.message ?? ''}`,
            error: e.message,
            severity: 'error',
          })
        }
      })
      .finally(() => {
        setIsFetching(false)
      })

    return () => {
      abortController.abort()
    }
  }, [letterOfDirectionPdf])

  return (
    <>
      {
        isFetching && <p>...generating prepopulated pdf letter...</p>
        // replace with animation later ... not necessary because generation is fast enough?
      }

      {fetchedPdf && (
        <Link
          href={`data:application/pdf;base64,${fetchedPdf}`}
          download="Gift of Securities - Letter of Direction.pdf"
          target="_blank"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Icons.FileOpen sx={{ marginRight: '.5rem' }} />
          Gift of Securities - Letter of Direction (PDF)
        </Link>
      )}

      <Alert {...alertProps} />
    </>
  )
}
